<template>
  <div class="content fade-in" style="min-height: 90vh;">

    <!-- ⏳ Global Loader -->
    <transition name="fade-slide">
      <div v-if="loading" class="global-loader">
        <div class="loader-spinner"></div>
        <p>{{ loaderText }}</p>
      </div>
    </transition>

    <!-- ===========================
        KPI SUMMARY (from API)
    ============================ -->
    <div class="kpi-summary">
      <div class="kpi-item" v-for="card in kpiCards" :key="card.title">
        <div class="kpi-info">
          <h4>{{ card.title }}</h4>
          <div class="value">{{ card.value }}</div>
        </div>
        <i :class="card.icon + ' kpi-icon'"></i>
      </div>
    </div>

    <!-- ===========================
        SEARCH + ADD MEMBER
    ============================ -->
    <div class="filter-row">
      <div class="search-box">
        <i class="bx bx-search"></i>
        <input
          type="text"
          v-model="searchQuery"
          placeholder="Search..."
          @input="resetPagination"
        />
      </div>

      <button class="btn-primary" @click="openAddMember">
        <i class="bx bx-plus"></i> Add New Member
      </button>
    </div>

    <!-- ===========================
        TEAM GRID
    ============================ -->
    <div class="team-grid">
      <div
        class="team-card fade-in"
        v-for="member in paginatedMembers"
        :key="member.user_id"
        :class="{ removing: deleteTarget === member.user_id }"
      >

        <!-- Header -->
        <div class="team-card-header">
          <div class="member-info">
            <img :src="defaultAvatar" alt="user" />

            <div>
              <h4>{{ member.fname }} {{ member.lname }}</h4>
              <p>{{ member.email }}</p>
              <p>{{ member.mobile }}</p>
            </div>
          </div>

          <!-- Dropdown -->
          <div class="menu-wrapper" @click.stop="toggleMenu(member.user_id)">
            <i class="bx bx-dots-horizontal-rounded"></i>

            <transition name="fade-slide">
              <div
                v-if="openMenu === member.user_id"
                class="dropdown-menu"
              >
                <button @click="openEditMember(member)">
                  <i class="bx bx-edit"></i> Edit Member
                </button>

                <button v-if="member.status==='1'" @click="toggleStatus(member)">
                  <i class="bx bx-refresh"></i> Deactive                
                </button>
                <button v-if="member.status==='0'" @click="toggleStatus(member)">
                  <i class="bx bx-refresh"></i> Active                
                </button>
                <button class="danger" @click="confirmRemove(member)">
                  <i class="bx bx-trash"></i> Delete
                </button>
              </div>
            </transition>
          </div>
        </div>

        <!-- Status -->
        <div class="role-section">
          <span class="status-badge"
                :class="statusClass(member.status)">
            {{ statusLabel(member.status) }}
          </span>
        </div>

      </div>
    </div>

    <!-- ===========================
        PAGINATION
    ============================ -->
    <div class="pagination-row">
      <span class="pagination-info">
        Showing {{ startIndex + 1 }}–{{ endIndex }} of {{ filteredTeam.length }}
      </span>

      <div class="pagination-buttons">
        <button :disabled="currentPage === 1" @click="prevPage">
          <i class="bx bx-chevron-left"></i>
        </button>

        <button
          v-for="page in totalPages"
          :key="page"
          @click="goToPage(page)"
          :class="{ active: currentPage === page }"
        >
          {{ page }}
        </button>

        <button :disabled="currentPage === totalPages" @click="nextPage">
          <i class="bx bx-chevron-right"></i>
        </button>
      </div>
    </div>

    <!-- ===========================
        CONFIRMATION DIALOG
    ============================ -->
    <ConfirmDialog
      :show="showConfirm"
      :title="confirmTitle"
      :message="confirmMessage"
      @cancel="showConfirm = false"
      @confirmAction="performDelete"
    />

    <!-- ===========================
        ADD / EDIT MEMBER MODAL
    ============================ -->
    <TeamMemberModal
      v-if="showMemberModal"
      :form="memberForm"
      :isEdit="isEditing"
      @close="showMemberModal = false"
      @save="saveMember"
    />

  </div>
</template>
<script>
import {
  getMyTeamList,
  addTeamMember,
  deleteUser,
  toggleUserStatus,
  getMyTeamReport,
} from "@/apis/aupadminapis";

import ConfirmDialog from "@/comps/ConfirmDialog.vue";
import TeamMemberModal from "@/comps/TeamMemberModal.vue";
import { useToast } from "vue-toastification";

export default {
  name: "TeamManagement",
  components: { ConfirmDialog, TeamMemberModal },

  data() {
    return {
      toast: useToast(),

      /* Loader */
      loading: false,
      loaderText: "",

      searchQuery: "",
      currentPage: 1,
      perPage: 6,

      openMenu: null,
      deleteTarget: null,

      /* Confirm dialog */
      showConfirm: false,
      confirmTitle: "",
      confirmMessage: "",
      memberToDelete: null,

      /* Modal */
      showMemberModal: false,
      isEditing: false,
      memberForm: {
        user_id: "",
        fname: "",
        lname: "",
        email: "",
        mobile: "",
        status: "1",
        password: "",
      },

      /* API DATA */
      team: [],
      kpiCards: [
        { title: "Total Members", value: 0, icon: "bx bx-group" },
        { title: "Active", value: 0, icon: "bx bx-user-check" },
        { title: "On Leave", value: 0, icon: "bx bx-coffee" },
        { title: "Deactivated", value: 0, icon: "bx bx-user-x" },
      ],

      /* avatar */
      defaultAvatar: "/assets/default-user.png",
    };
  },

  mounted() {
    this.fetchTeam();
    this.fetchStats();
  },

  computed: {
    filteredTeam() {
      return this.team.filter((m) =>
        [m.fname, m.lname, m.email, m.mobile, m.status]
          .join(" ")
          .toLowerCase()
          .includes(this.searchQuery.toLowerCase())
      );
    },

    totalPages() {
      return Math.ceil(this.filteredTeam.length / this.perPage);
    },

    startIndex() {
      return (this.currentPage - 1) * this.perPage;
    },

    endIndex() {
      return Math.min(this.startIndex + this.perPage, this.filteredTeam.length);
    },

    paginatedMembers() {
      return this.filteredTeam.slice(this.startIndex, this.endIndex);
    },
  },

  methods: {
    /* ===========================
         STATUS HELPERS
    ============================ */
    statusLabel(st) {
      if (st === "1") return "Active";
      if (st === "leave") return "On Leave";
      return "Deactivated";
    },

    statusClass(st) {
      return st === "1"
        ? "active"
        : st === "leave"
        ? "on-leave"
        : "deactivated";
    },

    /* ===========================
         LOAD TEAM LIST
    ============================ */
    async fetchTeam() {
      this.loading = true;
      this.loaderText = "Loading team...";
      const ldata = JSON.parse(sessionStorage.getItem("ldata")) || {};
      const res = await getMyTeamList(ldata.user_id, this.currentPage - 1, this.searchQuery);

      this.loading = false;

      if (res.status) {
        this.team = res.records || [];
      } else {
        this.toast.error(res.success || "Failed to load team");
      }
    },

    /* ===========================
         STATS (KPI)
    ============================ */
    async fetchStats() {
      const res = await getMyTeamReport();
      if (res.status) {
        this.kpiCards[0].value = res.total_members;
        this.kpiCards[1].value = res.active;
        this.kpiCards[2].value = res.on_leave;
        this.kpiCards[3].value = res.deactivated;
      }
    },

    /* ===========================
         PAGINATION
    ============================ */
    resetPagination() {
      this.currentPage = 1;
      this.fetchTeam();
    },
    nextPage() {
      if (this.currentPage < this.totalPages) {
        this.currentPage++;
        this.fetchTeam();
      }
    },
    prevPage() {
      if (this.currentPage > 1) {
        this.currentPage--;
        this.fetchTeam();
      }
    },
    goToPage(p) {
      this.currentPage = p;
      this.fetchTeam();
    },

    /* ===========================
         ADD / EDIT MEMBER
    ============================ */
    openAddMember() {
      this.isEditing = false;
      this.memberForm = {
        user_id: "",
        fname: "",
        lname: "",
        email: "",
        mobile: "",
        status: "1",
        password: "",
      };
      this.showMemberModal = true;
    },

    openEditMember(m) {
      this.isEditing = true;
      this.memberForm = {
        user_id: m.user_id,
        fname: m.fname,
        lname: m.lname,
        email: m.email,
        mobile: m.mobile,
        status: m.status,
        password: "",
      };
      this.showMemberModal = true;
    },

    async saveMember(form) {
      if (!form.fname.trim()) return this.toast.warning("First name required");
      if (!form.lname.trim()) return this.toast.warning("Last name required");
      if (!form.email.trim()) return this.toast.warning("Email required");
      if (!form.mobile.trim()) return this.toast.warning("Mobile required");

      if (!this.isEditing && !form.password.trim())
        return this.toast.warning("Password required");

      this.loading = true;
      this.loaderText = "Saving member...";
const ldata = JSON.parse(sessionStorage.getItem("ldata")) || {};
      const res = await addTeamMember(
        form.user_id,
        form.fname,
        form.lname,
        form.email,
        form.mobile,
        form.status,
        form.password,
        ldata.user_id
      );

      this.loading = false;

      if (res.status) {
        this.toast.success(res.success);
        this.showMemberModal = false;
        this.fetchTeam();
        this.fetchStats();
      } else {
        this.toast.error(res.success);
      }
    },

    /* ===========================
         DELETE MEMBER
    ============================ */
    confirmRemove(m) {
      this.confirmTitle = "Delete Member";
      this.confirmMessage = `Are you sure you want to delete ${m.fname} ${m.lname}?`;
      this.memberToDelete = m;
      this.showConfirm = true;
    },

    async performDelete() {
      this.showConfirm = false;
      this.deleteTarget = this.memberToDelete.user_id;

      this.loading = true;
      this.loaderText = "Deleting...";

      const res = await deleteUser(this.memberToDelete.user_id);

      this.loading = false;

      if (res.status) {
        this.toast.success("Member deleted");
        this.fetchTeam();
        this.fetchStats();
      } else {
        this.toast.error("Deletion failed");
      }

      this.deleteTarget = null;
    },

    /* ===========================
         TOGGLE STATUS
    ============================ */
    async toggleStatus(member) {
      this.loading = true;
      this.loaderText = "Updating status...";

      const res = await toggleUserStatus(member.user_id);

      this.loading = false;

      if (res.status) {
        this.toast.success(res.success);
        this.fetchTeam();
        this.fetchStats();
      } else {
        this.toast.error(res.success);
      }
    },

    toggleMenu(id) {
      this.openMenu = this.openMenu === id ? null : id;
    },
  },
};
</script>

<style scoped>
/* === GRID === */
.team-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 18px;
}

/* === CARD === */
.team-card {
  background: var(--card);
  border: 1px solid var(--border);
  border-radius: var(--radius);
  padding: 16px;
  display: flex;
  flex-direction: column;
  transition: all 0.3s ease;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.05);
}
.team-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.08);
}

.team-card-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
}

/* === MEMBER INFO === */
.member-info {
  display: flex;
  align-items: center;
  gap: 10px;
}
.member-info img {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  border: 1px solid var(--border);
}
.member-info h4 {
  font-weight: 600;
  font-size: 14px;
}
.member-info p {
  font-size: 12px;
  color: var(--muted);
}

/* === Role & Status === */
.role-section {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 12px 0;
}

.role-badge {
  font-size: 12px;
  font-weight: 600;
  padding: 4px 10px;
  border-radius: 8px;
  text-transform: capitalize;
}
.role-badge.admin { background: #e0e7ff; color: #1e3a8a; }
.role-badge.developer { background: #dbeafe; color: #1d4ed8; }
.role-badge.support { background: #fef3c7; color: #92400e; }

.status-badge {
  font-size: 12px;
  font-weight: 600;
  padding: 4px 8px;
  border-radius: 8px;
}
.status-badge.active { background: #dcfce7; color: #166534; }
.status-badge.active {
  background: #dcfce7;
  color: #166534;
}

.status-badge.on-leave {
  background: #fef9c3;
  color: #92400e;
}

.status-badge.deactivated {
  background: #fee2e2;
  color: #991b1b;
}

.status-badge.deactivated { background: #fee2e2; color: #991b1b; }
/* === Team Member Counts Grid (Alignment Fix) === */
.member-stats-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 10px;
  margin-top: 12px;
  align-items: stretch; /* ✅ ensures equal height */
}

.stat-item {
  background: #f9fafb;
  border: 1px solid var(--border);
  border-radius: 8px;
  padding: 10px 0;
  text-align: center;
  display: flex;
  flex-direction: column;
  justify-content: center;
  height: 70px; /* ✅ consistent block height */
  transition: all 0.3s ease;
}

.stat-item:hover {
  background: #f3f4f6;
  transform: translateY(-2px);
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.05);
}

.stat-item i {
  display: block;
  color: var(--primary);
  font-size: 1.2rem;
  margin-bottom: 4px;
  opacity: 0.85;
}

.stat-item span {
  font-size: 12px;
  color: var(--muted);
  line-height: 1.2;
}

.stat-item strong {
  font-size: 14px;
  font-weight: 700;
  color: var(--text);
  margin-top: 3px;
}

/* ✅ Responsive Adjustments */
@media (max-width: 1024px) {
  .member-stats-grid {
    grid-template-columns: repeat(2, 1fr) !important;
  }
}

@media (max-width: 480px) {
  .member-stats-grid {
    grid-template-columns: 1fr !important;
  }
  .stat-item {
    height: auto !important;
    padding: 8px 0 !important;
  }
}


/* === Footer === */
.team-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-top: 1px solid var(--border);
  padding-top: 10px;
  margin-top: 10px;
}
.last-login {
  font-size: 12px;
  color: var(--muted);
  display: flex;
  align-items: center;
  gap: 6px;
}
.btn-secondary {
  background: #f3f4f6;
  color: var(--text);
  border: none;
  border-radius: 8px;
  padding: 6px 12px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
}
.btn-secondary:hover {
  background: #e5e7eb;
}

/* === Dropdown === */
.menu-wrapper {
  position: relative;
  cursor: pointer;
}
.dropdown-menu {
  position: absolute;
  top: 24px;
  right: 0;
  background: #fff;
  border: 1px solid var(--border);
  border-radius: 8px;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.1);
  z-index: 10;
  display: flex;
  flex-direction: column;
  width: 160px;
}
.dropdown-menu button {
  border: none;
  background: transparent;
  text-align: left;
  padding: 10px 12px;
  font-size: 13px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: all 0.2s ease;
}
.dropdown-menu button:hover {
  background: #f3f4f6;
}
.dropdown-menu .danger {
  color: #dc2626;
}

/* === Animation === */
.fade-slide-enter-active,
.fade-slide-leave-active {
  transition: all 0.25s ease;
}
.fade-slide-enter-from,
.fade-slide-leave-to {
  opacity: 0;
  transform: translateY(-5px);
}

/* === Responsive === */
@media (max-width: 768px) {
  .team-grid { grid-template-columns: 1fr !important; }
  .team-card { padding: 14px; }
  .member-stats-grid { grid-template-columns: 1fr 1fr !important; }
  .filter-row { flex-direction: column; align-items: stretch; }
}

/* ===========================================================
   🌟 ADDITIONAL POLISH FOR SUPER ADMIN TEAM MANAGEMENT PAGE
   =========================================================== */

/* === Filters, Pagination, and KPI Section === */
.kpi-summary {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
  margin-bottom: 16px;
}
.kpi-item {
  background: var(--card);
  border: 1px solid var(--border);
  border-radius: var(--radius);
  padding: 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  transition: all 0.25s ease;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.04);
}
.kpi-item:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 12px rgba(0, 0, 0, 0.08);
}
.kpi-info h4 {
  color: var(--muted);
  font-size: 13px;
  margin-bottom: 4px;
}
.kpi-info .value {
  font-weight: 700;
  font-size: 18px;
  color: var(--text);
}
.kpi-icon {
  font-size: 20px;
  color: var(--primary);
  opacity: 0.85;
}

.filter-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 16px;
}
.search-box {
  display: flex;
  align-items: center;
  background: #fff;
  border: 1px solid var(--border);
  border-radius: 8px;
  padding: 6px 10px;
  flex: 1;
  min-width: 260px;
}
.search-box i {
  color: var(--muted);
  margin-right: 8px;
}
.search-box input {
  border: none;
  outline: none;
  width: 100%;
  font-size: 13px;
  color: var(--text);
}

.btn-primary {
  background: var(--primary);
  color: #fff;
  border: none;
  border-radius: 8px;
  padding: 8px 16px;
  font-size: 13px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  transition: all 0.3s ease;
}
.btn-primary:hover {
  background: var(--primary-dark);
  transform: translateY(-2px);
  box-shadow: 0 6px 12px rgba(0, 0, 0, 0.1);
}

/* === Pagination === */
.pagination-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 20px;
  padding-top: 14px;
  border-top: 1px solid var(--border);
  flex-wrap: wrap;
  gap: 10px;
}
.pagination-buttons {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
}
.pagination-buttons button {
  background: #f3f4f6;
  color: var(--text);
  border: none;
  padding: 6px 12px;
  border-radius: 6px;
  font-size: 13px;
  cursor: pointer;
  transition: all 0.3s ease;
}
.pagination-buttons button.active {
  background: var(--primary);
  color: #fff;
  font-weight: 600;
}
.pagination-buttons button:hover:not(.active) {
  background: #e5e7eb;
}

/* === Animation Utilities === */
.fade-in {
  animation: fadeInUp 0.5s ease forwards;
  opacity: 0;
  transform: translateY(8px);
}
@keyframes fadeInUp {
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.fade-slide-enter-active,
.fade-slide-leave-active {
  transition: all 0.25s ease;
}
.fade-slide-enter-from,
.fade-slide-leave-to {
  opacity: 0;
  transform: translateY(-5px);
}

/* === Scrollbar Styling === */
::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}
::-webkit-scrollbar-thumb {
  background: #d1d5db;
  border-radius: 8px;
}
::-webkit-scrollbar-thumb:hover {
  background: #9ca3af;
}
::-webkit-scrollbar-track {
  background: transparent;
}

/* === Hover Transitions === */
button,
.select-theme,
.search-box input {
  transition: all 0.25s ease-in-out;
}

button:hover,
.select-theme:focus {
  box-shadow: 0 0 0 3px rgba(17, 24, 39, 0.1);
}

/* === Empty State === */
.empty-state {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  color: var(--muted);
  font-size: 14px;
  padding: 40px 0;
}
.empty-state i {
  font-size: 36px;
  margin-bottom: 6px;
  opacity: 0.6;
}

/* === Loader (for Future API Integration) === */
.loader {
  width: 36px;
  height: 36px;
  border: 3px solid #e5e7eb;
  border-top-color: var(--primary);
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto;
}
@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* === Responsive Adjustments === */
@media (max-width: 1024px) {
  .team-grid {
    grid-template-columns: repeat(auto-fit, minmax(260px, 1fr)) !important;
    gap: 14px !important;
  }

  .filter-row {
    flex-direction: column !important;
    align-items: stretch !important;
    gap: 10px !important;
  }

  .search-box {
    width: 100% !important;
  }

  .btn-primary {
    width: 100% !important;
    justify-content: center !important;
  }

  .pagination-row {
    flex-direction: column !important;
    align-items: center !important;
  }

  .pagination-buttons {
    justify-content: center !important;
  }
}

@media (max-width: 768px) {
  .kpi-summary {
    grid-template-columns: 1fr 1fr !important;
    gap: 12px !important;
  }

  .team-card {
    padding: 14px !important;
  }

  .member-info img {
    width: 40px !important;
    height: 40px !important;
  }

  .member-info h4 {
    font-size: 13px !important;
  }

  .member-stats-grid {
    grid-template-columns: 1fr 1fr !important;
  }

  .team-footer {
    flex-direction: column !important;
    align-items: flex-start !important;
    gap: 10px !important;
  }

  .filter-row {
    flex-direction: column !important;
    gap: 12px !important;
  }

  .btn-primary {
    font-size: 0.95rem !important;
  }
}

@media (max-width: 480px) {
  .kpi-summary {
    grid-template-columns: 1fr !important;
  }

  .team-grid {
    grid-template-columns: 1fr !important;
  }

  .search-box input {
    font-size: 0.9rem !important;
  }

  .btn-primary {
    font-size: 0.85rem !important;
    padding: 0.7rem 1rem !important;
  }

  .pagination-info {
    font-size: 0.8rem !important;
  }

  .pagination-buttons button {
    font-size: 0.8rem !important;
    padding: 5px 8px !important;
  }
}

/* === Microinteraction: Dropdown Menu Glow === */
.dropdown-menu {
  animation: slideDown 0.2s ease forwards;
  transform-origin: top right;
}
@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-8px) scale(0.96);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

/* === Role Tag Animation === */
.role-badge {
  position: relative;
  overflow: hidden;
}
.role-badge::after {
  content: "";
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: rgba(255, 255, 255, 0.25);
  transform: skewX(-20deg);
  transition: 0.6s;
}
.role-badge:hover::after {
  left: 100%;
}

/* === Accessibility Enhancements === */
:focus-visible {
  outline: 2px solid var(--primary);
  outline-offset: 2px;
}
.status-badge.active {
  background: #dcfce7;
  color: #166534;
}

.status-badge.on-leave {
  background: #fef9c3;
  color: #92400e;
}

.status-badge.deactivated {
  background: #fee2e2;
  color: #991b1b;
}
/* Custom confirm modal */
.confirm-dialog-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.45);
  backdrop-filter: blur(4px);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 99999;
}

.confirm-dialog {
  background: #fff;
  width: 380px;
  padding: 22px;
  border-radius: 12px;
  border: 1px solid var(--border);
  animation: popIn 0.25s ease;
}

.confirm-title {
  font-size: 16px;
  font-weight: 700;
  margin-bottom: 6px;
}

.confirm-message {
  font-size: 14px;
  color: var(--muted);
  margin-bottom: 18px;
}

.confirm-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

.confirm-danger {
  background: #dc2626 !important;
  color: #fff !important;
}

@keyframes popIn {
  from { transform: scale(0.92); opacity: 0 }
  to { transform: scale(1); opacity: 1 }
}

/* Delete animation */
.team-card.removing {
  opacity: 0;
  transform: translateX(-20px);
  transition: all 0.3s ease;
}

</style>
